// The admin API host, overridable at runtime from the header's Network
// Settings dialog and persisted (device-wide, not per user) to localStorage.
//
// Every request in `$lib/api/adminAPI.ts` builds its URL from `adminApiBase()`,
// so a switch takes effect immediately for subsequent calls. The value is read
// once at module load; during SSR (or with storage unavailable) it stays at the
// built-in default.

import { browser } from '$app/environment';

// The server the app ships with — what "Reset default server" restores.
export const DEFAULT_ADMIN_API_BASE = 'https://news.primal.net/admin-api';

// Path appended when the user enters a bare host (e.g. "https://example.com").
const DEFAULT_API_PATH = '/admin-api';

const STORAGE_KEY = 'adminApiBase';

const read = (): string => {
  if (!browser) return DEFAULT_ADMIN_API_BASE;
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_ADMIN_API_BASE;
  } catch {
    return DEFAULT_ADMIN_API_BASE;
  }
};

let base = $state(read());

// Current API base URL, without a trailing slash. Called (not read as a value)
// so `adminAPI.ts` picks up a switch without re-importing.
export const adminApiBase = () => base;

export const isDefaultAdminApiBase = () => base === DEFAULT_ADMIN_API_BASE;

// Turn user input into an API base URL: keep the entered path if there is one
// (so a full ".../admin-api" URL works), otherwise append the default path to
// the origin. Query and hash are dropped. Throws on anything unparseable.
export const normalizeApiBase = (input: string): string => {
  const url = new URL(input.trim());

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error('unsupported protocol');
  }

  const path = url.pathname.replace(/\/+$/, '');
  return `${url.origin}${path || DEFAULT_API_PATH}`;
};

// Persist and apply a new base URL; returns the normalized value it stored.
export const setAdminApiBase = (input: string): string => {
  const next = normalizeApiBase(input);
  base = next;
  if (browser) {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // storage full/blocked — the override still applies for this session
    }
  }
  return next;
};

// Drop the override and go back to the shipped default.
export const resetAdminApiBase = (): string => {
  base = DEFAULT_ADMIN_API_BASE;
  if (browser) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
  return base;
};
